import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { ListCommentsResponseDto } from '@inference.engine/workspace-api-client/web';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import type { CommentAnnotationRef, CommentTargetRef } from '@/entities/project-comment';
import { isCommentsListKey, PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { syncFolderCommentCounts } from './sync-folder-comment-counts';
import { deleteComment } from './comments.api';
import { useTranslation } from 'react-i18next';

export { deleteComment };

interface UseDeleteCommentOptions {
  projectId: string;
  target: CommentTargetRef;
}

interface DeleteCommentVariables {
  commentId: string;
  parentId?: string | null;
  /** Live reply count of a root being deleted — its replies cascade server-side. */
  replyCount?: number;
}

export function useDeleteComment(options: UseDeleteCommentOptions) {
  const { t } = useTranslation('projects');

  const queryClient = useQueryClient();

  const { projectId, target } = options;

  return useMutation({
    mutationFn: ({ commentId }: DeleteCommentVariables) =>
      deleteComment(projectId, target, commentId),
    onSuccess: (_data, { commentId, parentId, replyCount = 0 }) => {
      const listFilter = {
        queryKey: PROJECT_COMMENT_QUERY_KEYS.list(projectId, target.targetType, target.targetId),
        predicate: (q: { queryKey: readonly unknown[] }) => isCommentsListKey(q.queryKey),
      };

      if (parentId) {
        queryClient.setQueryData<ListCommentsResponseDto>(
          PROJECT_COMMENT_QUERY_KEYS.replies(
            projectId,
            target.targetType,
            target.targetId,
            parentId,
          ),
          (old) =>
            old
              ? {
                  ...old,
                  items: old.items.filter((c) => c.id !== commentId),
                  total: Math.max(0, old.total - 1),
                }
              : old,
        );
        queryClient.setQueriesData<InfiniteData<ListCommentsResponseDto>>(listFilter, (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.map((c) =>
                    c.id === parentId ? { ...c, replyCount: Math.max(0, c.replyCount - 1) } : c,
                  ),
                })),
              }
            : old,
        );
      } else {
        queryClient.setQueriesData<InfiniteData<ListCommentsResponseDto>>(listFilter, (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((c) => c.id !== commentId),
                  total: Math.max(0, page.total - 1),
                })),
              }
            : old,
        );
        // Replies cascade with the root server-side, so drop its replies cache.
        queryClient.removeQueries({
          queryKey: PROJECT_COMMENT_QUERY_KEYS.replies(
            projectId,
            target.targetType,
            target.targetId,
            commentId,
          ),
        });
      }

      // A root takes its whole thread (root + replies); a reply takes just itself.
      syncFolderCommentCounts(queryClient, projectId, target, parentId ? -1 : -(1 + replyCount));

      queryClient.setQueryData<CommentAnnotationRef[]>(
        PROJECT_COMMENT_QUERY_KEYS.annotations(projectId, target.targetType, target.targetId),
        (old) => (old ? old.filter((a) => a.commentId !== commentId) : old),
      );

      // The deleted comment's version is unknown here, so refetch the (small)
      // version list to correct its root-comment count.
      if (target.targetType === 'file' && !parentId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FILE_QUERY_KEYS.versions(projectId, target.targetId),
        });
      }

      notificationService.showSuccess(t('commentOnFile.useDeleteComment.commentDeleted'), {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    },
    onError: (error) => {
      logger.error('Failed to delete comment:', error);
      notificationService.showError(t('commentOnFile.useDeleteComment.failedToDeleteComment'), {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    },
  });
}
